export type NavigationFeedbackState = {
  navigating: boolean
  fetching: boolean
}

type NavigationFeedbackListener = (state: NavigationFeedbackState) => void

const SHOW_AFTER_MS = 120
const MAX_NAVIGATION_MS = 12_000

let navPending = 0
let requestPending = 0
let listener: NavigationFeedbackListener | null = null
let shownNavigating = false
let shownFetching = false
let showTimer: ReturnType<typeof setTimeout> | null = null
let maxTimer: ReturnType<typeof setTimeout> | null = null

function totalPending() {
  return navPending + requestPending
}

function clearTimers() {
  if (showTimer) {
    clearTimeout(showTimer)
    showTimer = null
  }
  if (maxTimer) {
    clearTimeout(maxTimer)
    maxTimer = null
  }
}

function currentState(): NavigationFeedbackState {
  return {
    navigating: shownNavigating,
    fetching: shownFetching
  }
}

function notify() {
  listener?.(currentState())
}

function hideFeedback() {
  clearTimers()
  if (!shownNavigating && !shownFetching) return
  shownNavigating = false
  shownFetching = false
  notify()
}

function applyShownFromPending() {
  const nextNavigating = navPending > 0
  const nextFetching = requestPending > 0
  if (shownNavigating === nextNavigating && shownFetching === nextFetching) return
  shownNavigating = nextNavigating
  shownFetching = nextFetching
  notify()
}

function scheduleShow() {
  if (showTimer) return

  // Already visible for at least one mode — refresh flags immediately.
  if (shownNavigating || shownFetching) {
    applyShownFromPending()
    return
  }

  showTimer = setTimeout(() => {
    showTimer = null
    if (totalPending() <= 0) return
    applyShownFromPending()
    maxTimer = setTimeout(() => {
      navPending = 0
      requestPending = 0
      hideFeedback()
    }, MAX_NAVIGATION_MS)
  }, SHOW_AFTER_MS)
}

function sync() {
  if (totalPending() > 0) {
    scheduleShow()
    return
  }
  hideFeedback()
}

/** Subscribe the UI to show/hide. Returns an unsubscribe function. */
export const subscribeNavigationFeedback = (next: NavigationFeedbackListener | null) => {
  listener = next
  if (next && (shownNavigating || shownFetching)) next(currentState())
  return () => {
    if (listener === next) listener = null
  }
}

/** Begin route-transition feedback (link click, popstate, programmatic navigate). */
export const beginNavigationFeedback = () => {
  navPending += 1
  sync()
}

/** End one route-transition token. */
export const endNavigationFeedback = () => {
  if (navPending > 0) navPending -= 1
  sync()
}

/** Begin page-load GET feedback. */
export const beginRequestFeedback = () => {
  requestPending += 1
  sync()
}

/** End one page-load GET token. */
export const endRequestFeedback = () => {
  if (requestPending > 0) requestPending -= 1
  sync()
}

/** Show route-loading feedback (e.g. after leave is confirmed). */
export const startNavigationFeedback = () => {
  beginNavigationFeedback()
}

/**
 * Hide/cancel navigation feedback (e.g. while the unsaved-note modal is open,
 * or when the route has settled). Clears nav tokens only so in-flight GET
 * feedback can continue.
 */
export const stopNavigationFeedback = () => {
  navPending = 0
  sync()
}
